import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCents,
  PAYMENT_REQUEST_STATUS_LABELS,
  relativeTime,
  type PaymentRequest,
} from '@fsm/shared';
import { api } from '../lib/api';
import { Badge, Button, Card, Spinner } from './ui';

/**
 * Asking a customer to pay, and watching for the money.
 *
 * Deliberately a separate panel from "Payments". A payment is money that
 * arrived; a request is a question that may never be answered. Mixing them in
 * one list makes an invoice look part-paid when nothing has been paid at all.
 */

export function usePaymentCapabilities() {
  return useQuery({
    queryKey: ['payments', 'capabilities'],
    queryFn: () => api.payments.capabilities(),
    staleTime: 5 * 60_000,
    // No processor configured is an answer, not a failure worth retrying.
    retry: false,
  });
}

const TONE: Record<string, 'blue' | 'green' | 'red' | 'slate' | 'amber'> = {
  pending: 'amber',
  processing: 'blue',
  paid: 'green',
  failed: 'red',
  cancelled: 'slate',
  expired: 'slate',
};

export function PaymentRequestPanel({
  invoiceId,
  balanceCents,
  customerPhone,
}: {
  invoiceId: string;
  balanceCents: number;
  customerPhone: string | null;
}) {
  const qc = useQueryClient();
  const capabilities = usePaymentCapabilities();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ['payment-requests', invoiceId],
    queryFn: () => api.paymentRequests.list({ invoiceId }),
    select: (d) => d.items,
    // A customer paying is an event that happens elsewhere, so poll while
    // anything is still open. Stops on its own once nothing is.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(
        (r: PaymentRequest) => r.status === 'pending' || r.status === 'processing',
      )
        ? 15_000
        : false,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['payment-requests', invoiceId] });
    void qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };

  const create = useMutation({
    mutationFn: () => {
      const cents = amount.trim() ? Math.round(Number(amount) * 100) : null;
      return api.paymentRequests.create({ invoiceId, amountCents: cents });
    },
    onSuccess: () => {
      setAmount('');
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!capabilities.data?.capabilities.paymentLinks) return null;

  const open = (requests.data ?? []).filter(
    (r) => r.status === 'pending' || r.status === 'processing',
  );
  const history = (requests.data ?? []).filter(
    (r) => r.status !== 'pending' && r.status !== 'processing',
  );

  return (
    <Card title="Request payment" bodyClassName="p-0">
      {capabilities.data.isTestProvider && (
        <p className="border-b border-line bg-warn-soft px-4 py-2.5 text-[12.5px] text-warn">
          No payment processor is connected, so these links take test payments
          and move no money.
        </p>
      )}

      {balanceCents > 0 && (
        <div className="border-b border-line px-4 py-3">
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-[12px] text-ink-3">
                Amount — blank for the full {formatCents(balanceCents)}
              </span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder={(balanceCents / 100).toFixed(2)}
                className="h-9 w-full rounded-lg border border-line-2 bg-surface px-3 text-[13.5px] tnum"
              />
            </label>
            <Button
              variant="primary"
              size="sm"
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create link
            </Button>
          </div>
          {error && <p className="mt-2 text-[12.5px] text-danger">{error}</p>}
        </div>
      )}

      {requests.isLoading ? (
        <div className="grid place-items-center py-8">
          <Spinner className="size-5 text-ink-4" />
        </div>
      ) : (requests.data ?? []).length === 0 ? (
        <p className="px-4 py-8 text-center text-[13px] text-ink-4">
          No payment has been requested yet.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {[...open, ...history].map((request) => (
            <PaymentRequestRow
              key={request.id}
              request={request}
              customerPhone={customerPhone}
              isTest={capabilities.data.isTestProvider}
              onChanged={invalidate}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function PaymentRequestRow({
  request,
  customerPhone,
  isTest,
  onChanged,
}: {
  request: PaymentRequest;
  customerPhone: string | null;
  isTest: boolean;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState<string>('');

  const finish = (message: string) => {
    setNote(message);
    onChanged();
  };

  const send = useAction(() => api.paymentRequests.send(request.id), finish);
  const refresh = useAction(() => api.paymentRequests.refresh(request.id), finish);
  const cancel = useAction(() => api.paymentRequests.cancel(request.id), finish);
  const settle = useAction(() => api.paymentRequests.testSettle(request.id, 'paid'), finish);

  const isOpen = request.status === 'pending' || request.status === 'processing';

  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-2">
        <Badge tone={TONE[request.status] ?? 'slate'}>
          {PAYMENT_REQUEST_STATUS_LABELS[request.status]}
        </Badge>
        <span className="text-[14px] font-semibold tnum">
          {formatCents(request.settledCents ?? request.amountCents, request.currency)}
        </span>
        {request.overpaid && <Badge tone="amber">More than was owed</Badge>}
        <span className="ml-auto text-[12px] text-ink-4">
          {request.paidAt
            ? `Paid ${relativeTime(request.paidAt)}`
            : `Created ${relativeTime(request.createdAt)}`}
        </span>
      </div>

      {request.instrumentLabel && (
        <p className="mt-1 text-[12.5px] text-ink-3">{request.instrumentLabel}</p>
      )}
      {request.failureReason && (
        <p className="mt-1 text-[12.5px] text-danger">{request.failureReason}</p>
      )}

      {isOpen && (
        <>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-raised px-2 py-1.5 text-[11.5px] text-ink-3">
              {request.url}
            </code>
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(request.url);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              }}
              className="shrink-0 rounded border border-line px-2 py-1 text-[12px] font-medium hover:border-accent"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!customerPhone}
              title={customerPhone ? undefined : 'This customer has no phone number on file'}
              onClick={send.run}
              loading={send.pending}
            >
              {request.sentAt ? 'Text again' : 'Text the link'}
            </Button>
            {/* Webhooks get lost. This is the honest way to find out rather
                than telephoning the customer to ask whether they paid. */}
            <Button size="sm" onClick={refresh.run} loading={refresh.pending}>
              Check for payment
            </Button>
            <Button size="sm" onClick={cancel.run} loading={cancel.pending}>
              Cancel
            </Button>
            {isTest && (
              <Button size="sm" variant="primary" onClick={settle.run} loading={settle.pending}>
                Simulate payment
              </Button>
            )}
          </div>
        </>
      )}

      {note !== '' && <p className="mt-2 text-[12.5px] text-danger">{note}</p>}
    </li>
  );
}

/**
 * One button's worth of pending state.
 *
 * This row has four buttons that each hit a different endpoint and all report
 * failure the same way; four `useMutation` calls with four sets of options was
 * more ceremony than the behaviour deserves. Called unconditionally, in a fixed
 * order, like any other hook.
 */
function useAction(fn: () => Promise<unknown>, onDone: (message: string) => void) {
  const [pending, setPending] = useState(false);
  return {
    pending,
    run: async () => {
      setPending(true);
      try {
        await fn();
        onDone('');
      } catch (error) {
        onDone(error instanceof Error ? error.message : 'That did not work');
      } finally {
        setPending(false);
      }
    },
  };
}
